import React, { createContext, useState, useEffect, useContext } from 'react';
import { getColors, isLightMode, isPaperwhiteMode } from '../theme/colors';

const ThemeContext = createContext();

export { ThemeContext };

// Valid display modes
const VALID_MODES = ['dark', 'light', 'paperwhite'];

// Jet Age choice only. The old `displayMode` key was written on every visit
// while light was the default, so reading it kept cream on screens whose
// stylesheet is already dark. Do not copy that value over.
export const DISPLAY_MODE_KEY = 'brainstormy.jetage.displayMode';

function readSavedMode() {
  try {
    const saved = localStorage.getItem(DISPLAY_MODE_KEY);
    if (saved && VALID_MODES.includes(saved)) return saved;
  } catch (e) {
    // Private browsing can throw. Dark is the page default.
  }
  return 'dark';
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readSavedMode);

  // Derive colors from mode
  const colors = getColors(mode);

  useEffect(() => {
    // Apply mode to document for CSS variable switching
    document.documentElement.setAttribute('data-display-mode', mode);
    try {
      localStorage.setItem(DISPLAY_MODE_KEY, mode);
    } catch (e) {
      // Preference cannot be stored. The attribute above still paints this visit.
    }

    // Also set data-theme="jetage" in dark mode for backward compatibility
    // Many components rely on [data-theme="jetage"] CSS selectors
    if (mode === 'dark') {
      document.documentElement.setAttribute('data-theme', 'jetage');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }

    // Apply CSS variables for colors
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${camelToKebab(key)}`, value);
    });
  }, [mode, colors]);

  const setMode = (newMode) => {
    if (VALID_MODES.includes(newMode)) {
      setModeState(newMode);
    }
  };

  // Legacy support: toggleTheme cycles through modes
  const toggleTheme = () => {
    setModeState(prev => {
      const idx = VALID_MODES.indexOf(prev);
      return VALID_MODES[(idx + 1) % VALID_MODES.length];
    });
  };

  // Legacy support: theme and isDark for backward compatibility
  const theme = mode;
  const isDark = mode === 'dark';

  return (
    <ThemeContext.Provider value={{
      // New API
      mode,
      setMode,
      colors,
      isLight: isLightMode(mode),
      isPaperwhite: isPaperwhiteMode(mode),
      // Legacy API
      theme,
      setTheme: setMode,
      toggleTheme,
      isDark,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Theme context, or undefined outside a ThemeProvider (never throws). */
export function useOptionalTheme() {
  return useContext(ThemeContext);
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Helper to convert camelCase to kebab-case for CSS variables
function camelToKebab(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
